import React, { useCallback, useEffect, useRef, useState } from 'react';
import { videosData, VideosListForCategory } from "../../pages/SyndicatedVideos/SyndicatedVideos";
import '../SyndicatedVideosCards/SyndicatedVideosCards.scss';
import images from '../../assets/images';
import { formatDuration, getMenuItems, saveFileMetadata } from '../../utils/helper';
import MaterialActionsOverlay from '../ActionsOverlay/MaterialActionsOverlay';
import ConfirmationPopup from '../ConfirmationPopup/ConfirmationPopup';
import { useAppSelector } from '../../redux/app/hooks';
import { STUDENT_ROLE } from '../../utils/constants';
import { LinearProgress, Tooltip } from '@mui/material';
import { isPlatform } from '@ionic/react';
import { Buffer } from 'buffer';
import { checkFileStatusInDatabase, insertMaterial } from '../../database';

interface SyndicatedVideosCardsProps {
    handleOpenFile: (videosData: VideosListForCategory) => void;
    videosData: videosData[] | undefined;
    subCategory?: string;
    subject?: string;
    handleVideoActions: (data: VideosListForCategory, action: string) => void;
    reload: ()=> void;
}

const SyndicatedVideosCards: React.FC<SyndicatedVideosCardsProps> = ({ handleOpenFile, videosData, handleVideoActions, subCategory, subject, reload}) => {
    const { videoPlay, ShareWhiteIcon, MoreSmallWhiteIcon, ShareGif, DeleteGif, CloudWhiteDownload, DownloadFailed, DownloadSuccess } = images;
    const baseUrl = process.env.REACT_APP_FRONTEND_URL || '';
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [openOverlay, setOpenOverlay] = useState<boolean>(false);
    const [videoDurations, setVideoDurations] = useState<{ [key: string]: number }>({});
    const [selectedVideo, setSelectedVideo] = useState<VideosListForCategory | null>(null);
    const [downloadProgress, setDownloadProgress] = useState<{ [key: string]: number }>({});
    const [isDownloading, setIsDownloading] = useState<{ [key: string]: boolean }>({});
    const [downloadComplete, setDownloadComplete] = useState<{ [key: string]: boolean }>({});
    const [downloadFailed, setDownloadFailed] = useState<{ [key: string]: boolean }>({});
    const [action, setAction] = useState<string>('');
    const role = useAppSelector((state) => state.auth.role);
    const profile = useAppSelector((state) => state.auth.adminProfile);
    const [fileExists, setFileExists] = useState<{ [key: string]: boolean|undefined }>({});
    const videoNameRefs = useRef<(HTMLDivElement | null)[][]>([]);
    const [visibleTooltips, setVisibleTooltips] = useState<boolean[][]>([]);

    // Function to check if the file exists
const checkFileExistence = async (video: VideosListForCategory): Promise<boolean|undefined> => {
    if(window.electron && window.electron.isElectron()){
    try {
        const response = await window.electron.checkFileStatus(
            video.fileId,
            profile?.getCurrentUser?.nodes[0]?.uId,
            subCategory
        );

        console.log('File existence check response:', response);

        if (response && response.success !== undefined) {
            return response.exists; // Return the exists value (true/false)
        } else {
            console.error('Invalid response structure:', response);
            return false; // Default return value if the structure is invalid
        }
    } catch (error) {
        console.error('Error checking file existence:', error);
        return false; // Return false if there's an error during the check
    }
}else{
    console.warn('Platform not supported');
}
};

// Check the existence of each file and update the state
useEffect(() => {
    const checkFiles = async () => {
        const updatedFileExists: Record<string, boolean|undefined> = {};
        if (videosData && videosData.length > 0) {
            for (const videoItem of videosData) {
                if (videoItem.datas && videoItem.datas.length > 0) {
                    for (const video of videoItem.datas) {
                        const exists = await checkFileExistence(video);
                        console.log('File exists status for', video.filepath, ':', exists);
                        updatedFileExists[video.filepath] = exists; // Use the value from exists
                    }
                }
            }
            setFileExists(updatedFileExists);
        }
    };
    checkFiles();
}, [videosData, subCategory, profile]);

const checkFileExists = async (video: VideosListForCategory) => {
    if (window.cordova) {
        try {
            const response = await checkFileStatusInDatabase(
                video.fileId,
                profile?.getCurrentUser?.nodes[0]?.uId,
                subCategory
            );

            console.log('File existence response:', response);
            if (response && response.success) {
                console.log(response,'=======file existance response');
                return response.exists;
            }

            console.error('Failed to check file existence:', response);
            return false;
        } catch (err) {
            console.error('Error checking file existence:', err);
            return false;
        }
    } else {
        console.warn("Cordova is not available");
        return false;
    }
};

useEffect(() => {
    const checkFiles = async () => {
        const updatedFileExists: Record<string, boolean| undefined> = {}; // Define the type here
        if (videosData && videosData.length > 0) {
            for (const videoItem of videosData) {
                if (videoItem.datas && videoItem.datas.length > 0) {
                    for (const video of videoItem.datas) {
                        const exists = await checkFileExists(video);
                        console.log('File exists status for', video.filepath, ':', exists);
                        updatedFileExists[video.filepath] = exists; // Use the value from exists
                    }
                }
            }
            setFileExists(updatedFileExists);
        }

    };
    checkFiles();
}, [videosData, subCategory, profile]);

    //To get the duration of video
    const handleLoadedMetadata = (event: React.SyntheticEvent<HTMLVideoElement>, videoPath: string) => {
        const videoElement = event.currentTarget;
        const duration = videoElement.duration;
        setVideoDurations(prevDurations => ({
            ...prevDurations,
            [videoPath]: duration,
        }));
    };

    // click yes button in confirmation popup
    const onYesButtonClick = () => {
        setOpenConfirmationPopup(false);
        if (selectedVideo)
            handleVideoActions(selectedVideo, action)
    }

    // click no button in confirmation popup
    const onNoButtonClick = () => {
        setOpenConfirmationPopup(false);
    }
    const handleClickMoreIcon = (event: React.MouseEvent<HTMLElement>, video: VideosListForCategory) => {
        event.stopPropagation(); // Stop propagation to prevent triggering parent click
        setSelectedVideo(video);
        if (video?.isClassroom === true) {
            setAnchorEl(event.currentTarget);
            setOpenOverlay(true);
        } else if (video?.isClassroom === false) {
            setAction('share');
            setOpenConfirmationPopup(true);
        }
    };

    const handleCloseActions = () => {
        setAnchorEl(null);
        setOpenOverlay(false);
    };

    const handleDownload = async (video: VideosListForCategory) => {
        // if(isPlatform('android')){
        //     // try {
        //     //     const videoId = video?.filepath;
        //     //     const url = baseUrl + video?.filepath;
    
        //     //     setIsDownloading((prev) => ({ ...prev, [videoId]: true }));
        //     //     setDownloadProgress((prev) => ({ ...prev, [videoId]: 0 }));
        //     //     setDownloadComplete((prev) => ({ ...prev, [videoId]: false }));
        //     //     setDownloadFailed((prev) => ({ ...prev, [videoId]: false }));
    
        //     //     const fileName = video?.filepath.split('/').pop();
                
        //     //     const response = await fetch(url);
                
        //     //     if (!response.ok) {
        //     //         console.error('Failed to fetch the file', response.statusText);
        //     //         throw new Error('File download failed');
        //     //     }
    
        //     //     const blob = await response.blob();
                
        //     //     if(window?.cordova){
        //     //         window.resolveLocalFileSystemURL( window?.cordova?.file?.dataDirectory, (dir: any) => {
        //     //             dir.getFile(fileName, { create: true }, (fileEntry: any) => {
        //     //                 fileEntry.createWriter((fileWriter: any) => {
        //     //                     fileWriter.onwriteend = () => {
        //     //                         saveFileMetadata(fileName, { subCategory, subject });
        //     //                         fileEntry.file(() => {
        //     //                             console.log('File exists and is accessible');
        //     //                             setDownloadProgress((prev) => ({ ...prev, [videoId]: 100 })); // Set progress to 100%
        //     //                             setDownloadComplete((prev) => ({ ...prev, [videoId]: true }));
        //     //                                 setTimeout(() => {
        //     //                                 setDownloadComplete((prev) => ({ ...prev, [videoId]: false }));
        //     //                                 setIsDownloading((prev) => ({ ...prev, [videoId]: false }));
        //     //                                 setDownloadProgress((prev) => ({ ...prev, [videoId]: 0 }));
        //     //                             }, 2000);
        //     //                         }, (error: any) => {
        //     //                             console.error('File does not exist after write', error);
        //     //                             throw new Error('File verification failed');
        //     //                         });
        //     //                     };
        //     //                     fileWriter.onerror = (e: any) => {
        //     //                         console.error('File write failed', e);
        //     //                         setDownloadFailed((prev) => ({ ...prev, [videoId]: true }));
        //     //                         setTimeout(() => {
        //     //                             setDownloadFailed((prev) => ({ ...prev, [videoId]: false }));
        //     //                         }, 2000);
        //     //                     };
        //     //                     fileWriter.write(blob);
        //     //                 }, (error: any) => {
        //     //                     console.error('Failed to create writer', error);
        //     //                     throw new Error('File creation failed');
        //     //                 });
        //     //             }, (error: any) => {
        //     //                 console.error('Failed to get file entry', error);
        //     //                 throw new Error('File entry access failed');
        //     //             });
        //     //         });
        //     //     }
        //     // } catch (error) {
        //     //     console.error('Download error:', error);
        //     //     setIsDownloading((prev) => ({ ...prev, [video?.filepath]: false }));
        //     //     setDownloadFailed((prev) => ({ ...prev, [video?.filepath]: true }));
        //     //     setTimeout(() => {
        //     //         setDownloadFailed((prev) => ({ ...prev, [video?.filepath]: false }));
        //     //     }, 5000);
        //     // }

        //     try {
        //         const materialId = video?.filepath;
        //         const url = baseUrl + video?.filepath;
                
        //         setIsDownloading((prev) => ({ ...prev, [materialId]: true }));
        //         setDownloadProgress((prev) => ({ ...prev, [materialId]: 0 }));
        //         setDownloadComplete((prev) => ({ ...prev, [materialId]: false }));
        //         setDownloadFailed((prev) => ({ ...prev, [materialId]: false }));
                
        //         const response = await fetch(url);
        //         if (!response.ok) throw new Error('File download failed');
                
        //         const blob = await response.blob();
        //         const arrayBuffer = await blob.arrayBuffer();
        //         const fileData = new Uint8Array(arrayBuffer);

        //         console.log(fileData,'=====fileData');

        //         const fileSize = fileData.length;
        //         let downloadedSize = 0;
                
        //         // Insert material data into the database
        //         const materialData = {
        //             fileId: video.fileId,
        //             userId: profile?.getCurrentUser?.nodes[0]?.uId,
        //             filename: video.filename,
        //             filetype: 'video',
        //             filepath: video.filepath,
        //             filesize: video.filesize,
        //             filedata: fileData,
        //             fileLocation: subCategory,
        //             subject: subject,
        //         };

        //         console.log(materialData,'=====materialData');
                
        //         await insertMaterial(materialData);
    
        //         console.log('Download and metadata storage successful');

        //         const interval = setInterval(() => {
        //             if (downloadedSize < fileSize) {
        //                 downloadedSize += fileSize / 10; // Simulate 10% increments
        //                 const progress = Math.min(Math.floor((downloadedSize / fileSize) * 100), 95);
        //                 setDownloadProgress((prev) => ({ ...prev, [materialId]: progress }));
        //             } else {
        //                 clearInterval(interval);
    
        //                 // Set final progress to 100% and mark download as complete
        //                 setDownloadProgress((prev) => ({ ...prev, [materialId]: 100 }));
        //                 setDownloadComplete((prev) => ({ ...prev, [materialId]: true }));
        //                 reload();
        //                 // Reset state after a delay
        //                 setTimeout(() => {
        //                     setDownloadComplete((prev) => ({ ...prev, [materialId]: false }));
        //                     setIsDownloading((prev) => ({ ...prev, [materialId]: false }));
        //                     setDownloadProgress((prev) => ({ ...prev, [materialId]: 0 }));
        //                 }, 5000);
        //             }
        //         }, 300); // Simulate progress every 300ms
        //     } catch (error) {
        //         console.error('Download error:', error);
        //         setIsDownloading((prev) => ({ ...prev, [video.filepath]: false }));
        //         setDownloadFailed((prev) => ({ ...prev, [video.filepath]: true }));
        //         setTimeout(() => {
        //             setDownloadFailed((prev) => ({ ...prev, [video.filepath]: false }));
        //         }, 5000);
        //     }
        // }
         if (window?.electron && window.electron.isElectron()) {
            const materialId = video?.filepath;
            const url = baseUrl + video.filepath;
            try {
                console.log('materials==========>', video, 'subcategory=======>', subCategory);
    
                // Show initial download states
                setIsDownloading((prev) => ({ ...prev, [materialId]: true }));
                setDownloadProgress((prev) => ({ ...prev, [materialId]: 0 }));
                setDownloadComplete((prev) => ({ ...prev, [materialId]: false }));
                setDownloadFailed((prev) => ({ ...prev, [materialId]: false }));
    
                const response = await fetch(url);
                if (!response.ok) throw new Error('File download failed');
    
                // Get file blob
                const blob = await response.blob();
                const arrayBuffer = await blob.arrayBuffer();
                const fileData = Buffer.from(arrayBuffer);
    
                // Calculate total file size for progress tracking
                const fileSize = fileData.length;
                let downloadedSize = 0;
                // Send the file metadata and file data to the main process to store in SQLite
                const saveResponse = await window.electron.saveMaterial({
                    fileId: video.fileId,
                    userId: profile?.getCurrentUser?.nodes[0]?.uId,
                    filename: video.filename,
                    filetype: 'mp4',
                    filepath: video.filepath,
                    filesize: video.filesize,
                    filedata: fileData,
                    fileLocation: subCategory,
                    subject: subject,
                });

                if (saveResponse.success === false){
                    throw new Error(saveResponse.message || 'Error saving material to SQLite');
                }

                // Simulate progress: for Electron, the download is complete at once, so we simulate chunk progress
                const interval = setInterval(() => {
                    if (downloadedSize < fileSize) {
                        downloadedSize += fileSize / 10; // Simulate 10% increments
                        const progress = Math.min(Math.floor((downloadedSize / fileSize) * 100), 95);
                        setDownloadProgress((prev) => ({ ...prev, [materialId]: progress }));
                    } else {
                        clearInterval(interval);
    
                        // Set final progress to 100% and mark download as complete
                        setDownloadProgress((prev) => ({ ...prev, [materialId]: 100 }));
                        setDownloadComplete((prev) => ({ ...prev, [materialId]: true }));
                        reload();
                        // Reset state after a delay
                        setTimeout(() => {
                            setDownloadComplete((prev) => ({ ...prev, [materialId]: false }));
                            setIsDownloading((prev) => ({ ...prev, [materialId]: false }));
                            setDownloadProgress((prev) => ({ ...prev, [materialId]: 0 }));
                        }, 5000);
                    }
                }, 300); // Simulate progress every 300ms
    
                console.log('Download and metadata storage successful');
            } catch (error) {
                console.error('Download error:', error);
                setIsDownloading((prev) => ({ ...prev, [video.filepath]: false }));
                setDownloadFailed((prev) => ({ ...prev, [video.filepath]: true }));
                setTimeout(() => {
                    setDownloadFailed((prev) => ({ ...prev, [video.filepath]: false }));
                }, 5000);
            }
        } else {
            try {
                const videoId = video?.filepath;
                setIsDownloading((prev) => ({ ...prev, [videoId]: true }));
                setDownloadProgress((prev) => ({ ...prev, [videoId]: 0 }));
                setDownloadComplete((prev) => ({ ...prev, [videoId]: false }));
                setDownloadFailed((prev) => ({ ...prev, [videoId]: false }));
    
                const url = baseUrl + video?.filepath;
                const response = await fetch(url);
                if (!response.ok) {
                    console.error('Failed to fetch the file', response.statusText);
                    setIsDownloading((prev) => ({ ...prev, [videoId]: false }));
                    setDownloadFailed((prev) => ({ ...prev, [videoId]: true }));
                    setTimeout(() => {
                        setDownloadFailed((prev) => ({ ...prev, [videoId]: false }));
                    }, 5000);
                    return;
                }
    
                const reader = response.body?.getReader();
                let receivedLength = 0;
                const chunks = [];
    
                while (true) {
                    const { done, value } = await reader!.read();
                    if (done) break;
    
                    chunks.push(value);
                    receivedLength += value.length;
    
                    // Incremental progress based on the number of chunks
                    setDownloadProgress((prev) => ({ ...prev, [videoId]: Math.min(prev[videoId] + 5, 95) })); // Up to 95%
                }
    
                const blob = new Blob(chunks);
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = video?.filepath;
                link.click();
                // Add a small delay before updating the state
                await new Promise(resolve => setTimeout(resolve, 500));
                // setIsDownloading(false);
                setDownloadComplete((prev) => ({ ...prev, [videoId]: true }));
                setDownloadProgress((prev) => ({ ...prev, [videoId]: 100 })); // Set to 100% on completion
                // Hide the progress and complete message after 5 seconds
                setTimeout(() => {
                    setDownloadComplete((prev) => ({ ...prev, [videoId]: false }));
                    setIsDownloading((prev) => ({ ...prev, [videoId]: false }));
                    setDownloadProgress((prev) => ({ ...prev, [videoId]: 0 }));
                }, 5000);
            } catch (error) {
                console.error('Download error:', error);
                setIsDownloading((prev) => ({ ...prev, [video?.filepath]: false }));
                setDownloadFailed((prev) => ({ ...prev, [video?.filepath]: true }));
                setTimeout(() => {
                    setDownloadFailed((prev) => ({ ...prev, [video?.filepath]: false }));
                }, 5000);
            }
        }
    };

    const handleMouseEnter = (categoryIndex: number, videoIndex: number) => {
        const ref = videoNameRefs.current[categoryIndex]?.[videoIndex];
    
        if (ref) {
            const isOverflowing = ref.scrollWidth > ref.clientWidth;
            setVisibleTooltips((prev) => {
            const updated = [...prev];
            if (!updated[categoryIndex]) updated[categoryIndex] = [];
            updated[categoryIndex][videoIndex] = isOverflowing;
            return updated;
            });
        }
        };
    
        const handleMouseLeave = (categoryIndex: number, videoIndex: number) => {
        setVisibleTooltips((prev) => {
            const updated = [...prev];
            if (updated[categoryIndex]) updated[categoryIndex][videoIndex] = false;
            return updated;
        });
        };

    const menuItems = getMenuItems(handleCloseActions, handleVideoActions, setOpenConfirmationPopup, setAction, selectedVideo);


    const textRef = useRef<HTMLLabelElement>(null);
    const [isEllipsisActive, setIsEllipsisActive] = useState(false);
  
    const checkEllipsis = useCallback(() => {
      if (textRef.current) {
        setIsEllipsisActive(textRef.current.scrollWidth > textRef.current.clientWidth);
      }
    }, []);
  
    useEffect(() => {
      checkEllipsis();
      window.addEventListener("resize", checkEllipsis);
      return () => window.removeEventListener("resize", checkEllipsis);
    }, [checkEllipsis]);

    return (
        <div className={`${videosData && videosData.length > 0 ? 'syndicatedVideosCards' : 'noData'}`}>
            {videosData && videosData.length > 0 ? (
                videosData.map((videoItem: videosData, categoryIndex: number) => (
                    <div key={videoItem.categoryOriginalName} className="videoWithCategoryCtn">
                        <Tooltip title={isEllipsisActive ? textRef.current?.textContent || "" : ""}>
      <label
        ref={textRef}
        className="videoCategory">
        {videoItem.categoryOriginalName} - {videoItem.totalCount} {videoItem.totalCount > 1 ? "Videos" : "Video"}
      </label>
    </Tooltip>
                        <div className="videoCardsList">
                            {videoItem.datas.map((video: VideosListForCategory, videoIndex: number) => (
                                <div key={video.filepath} className="videoCard"
                                    onClick={() => handleOpenFile(video)}
                                    style={{
                                        backgroundImage: `url(${video.coverImagePath ? baseUrl + video.coverImagePath : videoPlay})`,
                                        backgroundColor: video.coverImagePath ? 'transparent' : '#e2dfdf',
                                    }}
                                >  
                                    {(videoDurations[video.filepath] && !isNaN(videoDurations[video.filepath])) ?
                                        <div className='duration'>{formatDuration(videoDurations[video.filepath])}</div>
                                        :
                                        <div className='duration'>0:00</div>
                                    }
                                    <video
                                        src={baseUrl + video.filepath}
                                        muted
                                        autoPlay={false}
                                        controls={false}
                                        loop
                                        playsInline
                                        className='videoPlayer'
                                        onLoadedMetadata={(event) => handleLoadedMetadata(event, video.filepath)}
                                    />
                                    {/* {
                                        video?.coverImagePath ?
                                          <img src={video?.coverImagePath ? baseUrl + video?.coverImagePath : videoPlay} className='videoThumbnail' />
                                        :
                                        <div className='ImgCtn'>
                                           <img src={video?.coverImagePath ? baseUrl + video?.coverImagePath : videoPlay} className='videoThumbnail' />
                                        </div>
                                    } */}
                                    <div className='video-inner'>
                                    <div className="videoCardProgressCtn">
                                        {(isDownloading[video?.filepath] && role === STUDENT_ROLE) && (
                                            <LinearProgress variant="determinate" value={downloadProgress[video?.filepath]}
                                                style={{ backgroundColor: '#ECECEC', height: '2px' }}
                                                sx={{
                                                    '& .MuiLinearProgress-bar': {
                                                        backgroundColor: '#4086F4',
                                                        height: '2px'
                                                    },
                                                }} />
                                        )} 
                                    </div>
                                        <div className='actionsCtn'>
                                        <Tooltip
                                                title={video?.filename}
                                                open={visibleTooltips[categoryIndex]?.[videoIndex] || false}
                                                placement="bottom"
                                                disableInteractive
                                            >
                                                <div className='videoName' 
                                                    ref={(el) => {
                                                        if (!videoNameRefs.current[categoryIndex]) {
                                                            videoNameRefs.current[categoryIndex] = [];
                                                        }
                                                        videoNameRefs.current[categoryIndex][videoIndex] = el;
                                                    }}
                                                    onMouseEnter={() => handleMouseEnter(categoryIndex, videoIndex)}
                                                    onMouseLeave={() => handleMouseLeave(categoryIndex, videoIndex)}
                                                >
                                                    {video?.filename}
                                                </div>
                                            </Tooltip>
                                                {/* {(downloadComplete[video?.filepath] && role === STUDENT_ROLE) && ( */}
                                                    {/* <div className="downloadCompleteMessage">
                                                        {downloadComplete[video?.filepath] ? "Downloaded" : downloadFailed[video?.filepath] ? "Not downloaded" : "Downloaded"}
                                                    </div> */}
                                                {/* )} */}
                                            <div className='actionBtn' onClick={(event) => handleClickMoreIcon(event, video)}>
                                                {role === STUDENT_ROLE ?
                                                    // <div className="shareBtn" onClick={(event) => { event.stopPropagation(); handleDownload(video); }}>
                                                    //     <img src={downloadComplete[video?.filepath] ? DownloadSuccess: downloadFailed[video?.filepath] ? DownloadFailed : CloudWhiteDownload} alt="download" />
                                                    // </div> 
                                                    <div
                                                        className="materialDownload"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (!fileExists[video?.filepath]) {
                                                                handleDownload(video);
                                                            } else {
                                                                handleOpenFile(video);
                                                            }
                                                        }}
                                                    >
                                                        {downloadComplete[video?.filepath] ? (
                                                            <img src={DownloadSuccess} alt="DownloadSuccess" />
                                                        ) : downloadFailed[video?.filepath] ? (
                                                            <img src={DownloadFailed} alt="DownloadFailed" />
                                                        ) : fileExists[video?.filepath] ? (
                                                            <img src={DownloadSuccess} alt="AlreadyDownloaded" />
                                                        ) : (
                                                            <img src={CloudWhiteDownload} alt="CloudDownload" />
                                                        )}
                                                    </div>
                                                    :
                                                    <div className={video?.isClassroom ? 'actionBtn' : 'shareBtn'} onClick={(event) => handleClickMoreIcon(event, video)}>
                                                        {video?.isClassroom ?
                                                            <img src={MoreSmallWhiteIcon} alt="action" className='moreIcon' />
                                                            :
                                                            <img src={ShareWhiteIcon} alt="share" className='shareIcon' />
                                                        }
                                                    </div>}
                                            </div>
                                    </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))
            ) : (
                <div className="noDataFoundStyle">No data found</div>
            )}
            <MaterialActionsOverlay
                anchorEl={anchorEl}
                open={openOverlay}
                handleClose={handleCloseActions}
                menuItems={menuItems}
                actionsHeight={'90px'}
                position="left"
            />
            <ConfirmationPopup
                open={openConfirmationPopup}
                message={action === "share" ? `Are you sure you want to Share this file with your Class?` : `Are you sure you want to Delete this file from the Classroom?`}
                gifData={action === "share" ? ShareGif : DeleteGif}
                yesButtonFunctionality={onYesButtonClick}
                noButtonFunctionality={onNoButtonClick}
                messageWidth={'195px'}
            />
        </div>
    );
};

export default SyndicatedVideosCards;
